import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  enterpriseAdminService,
  EnterpriseDetails,
} from '../services/enterpriseAdminService';
import {
  enterpriseService,
  LinkedEnterprise,
} from '../services/enterpriseService';
import { useAuth } from './AuthContext';
import { ROLES } from '../constants/roles';

export type ViewType = 'full' | 'fasttrack';

interface EnterpriseContextType {
  enterpriseDetails: EnterpriseDetails | null;
  selectedQuarterId: string | undefined;
  setSelectedQuarterId: (quarterId: string) => void;
  viewType: ViewType;
  setViewType: (viewType: ViewType) => void;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  linkedEnterprises: LinkedEnterprise[];
  selectedEnterprise: LinkedEnterprise | null;
  setSelectedEnterprise: (enterprise: LinkedEnterprise | null) => void;
  isLoadingEnterprises: boolean;
}

const EnterpriseContext = createContext<EnterpriseContextType | undefined>(
  undefined
);

export const EnterpriseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [enterpriseDetails, setEnterpriseDetails] =
    useState<EnterpriseDetails | null>(null);
  const [selectedQuarterId, setSelectedQuarterId] = useState<
    string | undefined
  >(undefined);
  const [viewType, setViewType] = useState<ViewType>('full');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [linkedEnterprises, setLinkedEnterprises] = useState<
    LinkedEnterprise[]
  >([]);
  const [selectedEnterprise, setSelectedEnterprise] =
    useState<LinkedEnterprise | null>(null);
  const [isLoadingEnterprises, setIsLoadingEnterprises] = useState(false);
  const { setEnterpriseName, user } = useAuth();

  // Fetch linked enterprises on mount for enterprise admin users only
  useEffect(() => {
    if (!user || user.role !== ROLES.ENTERPRISE_ADMIN) return;
    const fetchLinkedEnterprises = async () => {
      setIsLoadingEnterprises(true);
      try {
        const response = await enterpriseService.getUserLinkedEnterprises();
        const enterprises = response?.data;
        if (enterprises && enterprises.length > 0) {
          setLinkedEnterprises(enterprises);
          setSelectedEnterprise(prev => prev ?? enterprises[0]);
        }
      } catch (err) {
        console.error('Error fetching linked enterprises:', err);
      } finally {
        setIsLoadingEnterprises(false);
      }
    };
    fetchLinkedEnterprises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchEnterpriseDetails = async (enterpriseId?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response =
        await enterpriseAdminService.getEnterpriseDetails(enterpriseId);
      if (response) {
        setEnterpriseDetails(response);
        if (user && user.enterprise_name !== response.name) {
          setEnterpriseName(response.name || '');
        }
        // Set initial selected quarter to the latest active quarter
        if (response.available_quarters) {
          const latestQuarter = response.available_quarters.find(
            q => q.is_latest
          );
          if (latestQuarter) {
            setSelectedQuarterId(latestQuarter.id);
          }
        }
      } else {
        // Handle null response (400 error - no enterprise)
        setEnterpriseDetails(null);
        setEnterpriseName('');
        setSelectedQuarterId(undefined);
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching enterprise details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch enterprise details for enterprise admin users once the selected enterprise is known
    if (
      user &&
      user.role === ROLES.ENTERPRISE_ADMIN &&
      selectedEnterprise?.id
    ) {
      fetchEnterpriseDetails(selectedEnterprise.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, selectedEnterprise?.id]);

  const value: EnterpriseContextType = {
    enterpriseDetails,
    selectedQuarterId,
    viewType,
    setViewType,
    isLoading,
    error,
    setSelectedQuarterId,
    refetch: () =>
      selectedEnterprise?.id
        ? fetchEnterpriseDetails(selectedEnterprise.id)
        : Promise.resolve(),
    linkedEnterprises,
    selectedEnterprise,
    setSelectedEnterprise,
    isLoadingEnterprises,
  };

  return (
    <EnterpriseContext.Provider value={value}>
      {children}
    </EnterpriseContext.Provider>
  );
};

export const useEnterprise = (): EnterpriseContextType => {
  const context = useContext(EnterpriseContext);
  if (context === undefined) {
    throw new Error('useEnterprise must be used within an EnterpriseProvider');
  }
  return context;
};
